/**
 * Role-Based Access Control (RBAC) Context
 *
 * This module provides the core access control functionality for the entire application.
 * It establishes a simplified role system with only two roles:
 * - STUDENT: Regular students with basic access
 * - AMBASSADOR: Teachers/educators/professionals with advanced capabilities
 *
 * Key features:
 * - Simple role hierarchy (AMBASSADOR > STUDENT)
 * - Permission-based access control for granular feature access
 * - Integration with Firebase authentication and user profiles
 * - Context-based distribution of role information throughout the app
 *
 * The role system is used to control:
 * - Route access (which pages users can visit)
 * - UI element visibility (buttons, features, navigation items)
 * - Action permissions (what operations users can perform)
 * - Content access (what information users can view)
 */

import React, { createContext, useContext, useState, useEffect } from 'react'
import { useAuth } from '../firebase/auth'
import { hasPermission, mapStatusToRole } from './roles'

/**
 * Role Context - Central store for role and permission information
 *
 * This context provides role information and permission checking capabilities
 * to all components in the application.
 *
 * @type {React.Context}
 */
const RoleContext = createContext({
  userRole: null,
  userProfile: null,
  isLoading: true,
  hasRole: () => false,
  hasPermission: () => false,
})

/**
 * RoleProvider Component
 *
 * Provides role-based context to the entire application. This component:
 * - Maps user status from Firebase to application roles
 * - Provides role checking functionality
 * - Provides permission checking functionality
 * - Handles loading states during role determination
 *
 * This provider is a critical part of the application's security infrastructure
 * and affects what users can see and do throughout the entire application.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to wrap with role context
 * @returns {JSX.Element} Provider component
 */
export const RoleProvider = ({ children }) => {
  const { authUser, userProfile, isLoading: authLoading } = useAuth()
  const [userRole, setUserRole] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  /**
   * Effect hook to update user role based on authentication state
   *
   * This effect runs whenever the auth state or user profile changes.
   * It maps the user's status from their profile to an application role
   * using the mapStatusToRole function from roles.js.
   */
  useEffect(() => {
    if (authLoading) {
      setIsLoading(true)
      return
    }

    if (!authUser || !userProfile) {
      setUserRole(null)
      setIsLoading(false)
      return
    }

    // Map user status to role
    const role = mapStatusToRole(userProfile.status)
    setUserRole(role)
    setIsLoading(false)
  }, [authUser, userProfile, authLoading])

  /**
   * Function to check if user has a specific role or higher
   *
   * This implements the simplified role hierarchy, where AMBASSADOR role
   * inherits permissions from STUDENT role. The hierarchy is:
   * AMBASSADOR > STUDENT
   *
   * For example, an AMBASSADOR also has all STUDENT permissions.
   *
   * @param {string} roleToCheck - The role to check
   * @returns {boolean} - True if user has the role or higher
   */
  const hasRole = (roleToCheck) => {
    if (!userRole) return false

    // Direct role match
    if (userRole === roleToCheck) return true

    // Simple role hierarchy: AMBASSADOR > STUDENT
    if (roleToCheck === 'STUDENT') {
      return ['STUDENT', 'AMBASSADOR'].includes(userRole)
    }

    if (roleToCheck === 'AMBASSADOR') {
      return userRole === 'AMBASSADOR'
    }

    return false
  }

  /**
   * Function to check if user has a specific permission
   *
   * This delegates to the hasPermission function from roles.js,
   * which maintains the mapping between roles and permissions.
   *
   * @param {string} permission - The permission to check
   * @returns {boolean} - True if user has the permission
   */
  const checkPermission = (permission) => {
    if (!userRole || !userProfile) return false
    // Pass the raw user status to hasPermission, not the mapped role
    return hasPermission(userProfile.status, permission)
  }

  // Context value with role information and helper functions
  const value = {
    userRole,
    userProfile,
    isLoading,
    hasRole,
    hasPermission: checkPermission,
    // Alias for backward compatibility
    role: userRole,
  }

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>
}

/**
 * Custom hook to access the role context
 *
 * This hook provides access to role information and permission checking
 * throughout the application. It's used by:
 * - Route protection components to control page access
 * - UI components to show/hide features based on roles
 * - Business logic to enable/disable actions based on permissions
 *
 * Example usage:
 * ```
 * const { hasRole, hasPermission } = useRole();
 * if (hasRole('AMBASSADOR')) {
 *   // Show ambassador features
 * }
 * ```
 *
 * @returns {Object} Role context with userRole, hasRole(), and hasPermission()
 * @throws {Error} If used outside of a RoleProvider
 */
export const useRole = () => {
  const context = useContext(RoleContext)
  if (context === undefined) {
    throw new Error('useRole must be used within a RoleProvider')
  }
  return context
}

export default RoleContext
